import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CloudDivider } from "@/components/common";
import { HelpCircle, MessageCircle, RefreshCw } from "lucide-react";

export default function Faq() {
  const { t, pick } = useI18n();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error

  const wa = settings?.whatsapp || {};
  const number = (wa.contact_number || "").replace(/[^0-9]/g, "");
  const waHref = number ? `https://wa.me/${number}` : "";

  const load = () => {
    setState("loading");
    api
      .get("/faqs")
      .then((r) => { setItems(r.data || []); setState("ready"); })
      .catch(() => setState("error"));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center">
          <HelpCircle className="h-6 w-6 text-[hsl(var(--brand-bronze))]" />
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-[hsl(var(--brand-ink))]" data-testid="faq-page-title">
          {t("faq.title")}
        </h1>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          {t("faq.subtitle")}
        </p>
      </div>

      <CloudDivider className="my-6" />

      {/* Loading */}
      {state === "loading" && (
        <div className="space-y-3" data-testid="faq-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      )}

      {/* Error */}
      {state === "error" && (
        <div className="text-center py-14" data-testid="faq-error">
          <p className="text-muted-foreground">{t("faq.error")}</p>
          <Button variant="outline" className="mt-4 border-[hsl(var(--brand-gold))]" onClick={load} data-testid="faq-retry-button">
            <RefreshCw className="h-4 w-4 mr-1" /> {t("faq.retry")}
          </Button>
        </div>
      )}

      {/* Empty */}
      {state === "ready" && items.length === 0 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="faq-empty">
          {t("faq.empty")}
        </div>
      )}

      {/* List */}
      {state === "ready" && items.length > 0 && (
        <Card className="border-border bg-card">
          <CardContent className="p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {items.map((it) => (
                <AccordionItem key={it.id} value={it.id} data-testid={`faq-item-${it.id}`} className="border-border">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${it.id}`}
                    className="text-left text-[15px] sm:text-base font-semibold text-[hsl(var(--brand-ink))] hover:no-underline hover:text-[hsl(var(--brand-vermillion))]"
                  >
                    <span className="flex items-start gap-2">
                      <span className="mt-0.5 text-[hsl(var(--brand-bronze))] font-display shrink-0">Q</span>
                      <span>{pick(it, "question")}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${it.id}`}
                    className="text-sm sm:text-[15px] leading-7 text-foreground/85"
                  >
                    <div className="flex items-start gap-2 pl-1">
                      <span className="mt-0.5 text-[hsl(var(--brand-vermillion))] font-display shrink-0">A</span>
                      <span className="whitespace-pre-line">{pick(it, "answer")}</span>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Contact CTA */}
      {state === "ready" && waHref && (
        <div className="mt-10 rounded-2xl bg-[hsl(var(--secondary))] border border-border px-6 py-8 text-center">
          <h2 className="font-display text-xl font-semibold text-[hsl(var(--brand-ink))]">{t("faq.stillQuestions")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("faq.contactUs")}</p>
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp-cta">
            <Button className="mt-4 bg-[hsl(var(--primary))] text-primary-foreground">
              <MessageCircle className="h-4 w-4 mr-1" /> {t("common.whatsapp")}
            </Button>
          </a>
        </div>
      )}
    </div>
  );
}
